/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * InboxFlow — the top-level container for the inbox. Sets up the inbox
 * context provider and a single stack navigator over the screen map (see
 * `./screens`); the `_index` screen branches mobile vs desktop internally.
 * The inbox is a composition of two thread streams — feed activity + chats.
 */

import { AppNavigationProvider } from '@euler/app/components/AppNavigationProvider';
import { useStatusBarStyle } from '@euler/app/components/AppStatusBar';
import { ChatContactsProvider } from '@euler/app/flows/chat/lib/contacts/provider';
import { ChatThreadsProvider } from '@euler/app/flows/chat/lib/threads/provider';
import { ChatTransport } from '@euler/app/flows/chat/lib/transport';
import { useWhiteHeaderStackOptions } from '@euler/app/navigation/whiteHeaderStackOptions';
import { ErrorFallback } from '@euler/components/error';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { adaptiveWrapperOptions } from '@euler/functions/navigationOptionsBuilder';
import { spreadScreens } from '@euler/functions/spreadScreens';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ScopedObservableSuspenseResourceContextProvider } from '@euler/utils/hooks';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { createStackNavigator } from '@react-navigation/stack';
import * as Sentry from '@sentry/react';
import { memo } from 'react';
import { InboxProvider } from './provider';
import { InboxNavParams, Screens } from './screens';

const Stack = createStackNavigator();

type Props = {
  initialScreen?: keyof InboxNavParams;
};

export const InboxFlow = wrapNavigatorScreen(
  (props: Props) => {
    useStatusBarStyle('dark');
    return <Content {...props} />;
  },
  adaptiveWrapperOptions({ presentation: 'card' }),
);

const Content = memo((props: Props) => {
  // Clean white headers across all inbox sub-screens — matches the inbox
  // landing page (which sets headerShown:false but uses the same palette), and
  // the app-level chat flow the conversation now lives in.
  const screenOptions = useWhiteHeaderStackOptions();
  // One stack for both form factors. The `_index` screen itself branches
  // mobile (chrome + thread tabs) vs desktop (columned master-detail); the
  // chat/feed sub-screens are pushed on mobile and embedded by the desktop
  // layout. The chat stack is inbox-scoped: `ChatTransport` owns the WS
  // connection, and the thread/contact lists + WS reducer live in their
  // providers here, not app-wide.
  return (
    <AppNavigationProvider>
      <LayoutProviderView fallbackToChildren style={{ flex: 1 }}>
        <Sentry.ErrorBoundary
          // @ts-ignore
          fallback={(params: any) => <ErrorFallback {...params} />}
        >
          <ScopedObservableSuspenseResourceContextProvider scope={'inbox'}>
            <BottomSheetModalProvider>
              <ChatTransport>
                <ChatThreadsProvider>
                  <ChatContactsProvider>
                    <InboxProvider>
                      <Stack.Navigator
                        initialRouteName={props.initialScreen ?? '_index'}
                        screenOptions={screenOptions}
                      >
                        {spreadScreens(
                          Stack.Screen,
                          Screens,
                          props.initialScreen ?? '_index',
                          {},
                        )}
                      </Stack.Navigator>
                    </InboxProvider>
                  </ChatContactsProvider>
                </ChatThreadsProvider>
              </ChatTransport>
            </BottomSheetModalProvider>
          </ScopedObservableSuspenseResourceContextProvider>
        </Sentry.ErrorBoundary>
      </LayoutProviderView>
    </AppNavigationProvider>
  );
});
